import {
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import { AutoComplete, Spin } from 'antd'

import { Label } from 'common/components/Label/Label'

import { useSearchChiefComplaintsQuery } from 'features/Schedule/api/chiefComplaints.api'
import {
  COMPLAINT_MAX_LENGTH,
  normalizeComplaint,
  toComplaintText,
} from 'features/Schedule/api/chiefComplaints.transform'
import type { ComplaintSuggestions } from 'features/Schedule/api/chiefComplaints.types'

import styles from './chiefComplaints.module.scss'
import type { ComplaintEntry, ComplaintListSnapshot } from './complaintField.types'
import { ComplaintChips } from './ComplaintChips'
import { hasComplaint } from './complaintList'
import { COMPLAINT_TEXT, lockLine, useChiefComplaints } from './useChiefComplaints'

const SEARCH_DELAY_MS = 200
const ARROW_KEYS = new Set(['ArrowUp', 'ArrowDown'])

export type ChiefComplaintsFieldProps = {
  mode: 'create' | 'edit'
  siteId: string
  patientId: number | null
  appointmentId: number | null
  disabled: boolean
  // Reason used when the dialog itself knows the booking is locked (checked out) and the server said nothing.
  lockedReason?: string
  listRef: RefObject<ComplaintListSnapshot>
  // The server lock line, so Comments can lock with the complaints; null when open.
  onLockChange?: (line: string | null) => void
}

type Option = { value: string; label: string }
type Group = { label: string; options: Option[] }

// First spelling of each complaint not shown above; chips stay listed, as in OpenEMR.
const freshOptions = (texts: readonly string[], seen: ReadonlySet<string>): Option[] =>
  texts
    .filter((text, index) => {
      const norm = normalizeComplaint(text)
      return (
        !seen.has(norm) && texts.findIndex((other) => normalizeComplaint(other) === norm) === index
      )
    })
    .map((text) => ({ value: text, label: text }))

const buildOptions = (suggestions: ComplaintSuggestions | undefined): Group[] => {
  const patient = freshOptions(suggestions?.patient ?? [], new Set())
  const shown = new Set(patient.map((option) => normalizeComplaint(option.value)))
  const practice = freshOptions(suggestions?.practice ?? [], shown)
  return [
    { label: COMPLAINT_TEXT.patientGroup, options: patient },
    { label: COMPLAINT_TEXT.practiceGroup, options: practice },
  ].filter((group) => group.options.length)
}

export const ChiefComplaintsField = ({
  mode,
  siteId,
  patientId,
  appointmentId,
  disabled,
  lockedReason,
  listRef,
  onLockChange,
}: ChiefComplaintsFieldProps) => {
  const inputId = useId()
  const [draft, setDraft] = useState('')
  const list = useChiefComplaints({ mode, siteId, patientId, appointmentId, listRef, draft })
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const pickedRef = useRef(false)
  const arrowedRef = useRef(false)
  // State copy of arrowedRef: the popup shows an active row only once the arrows were used.
  const [hasArrowed, setHasArrowed] = useState(false)
  const enterRef = useRef(false)

  const serverLine = list.frozen ? lockLine(list.frozenReason ?? COMPLAINT_TEXT.checkedOut) : null
  const isReadOnly = disabled || list.frozen || !!list.loadHint || list.isPending
  // A plain disabled dialog (another status) says nothing; only a known lock explains itself.
  const lockReason = list.frozen
    ? list.frozenReason ?? COMPLAINT_TEXT.checkedOut
    : disabled
    ? lockedReason
    : undefined
  const hintLine = lockReason ? lockLine(lockReason) : list.loadHint

  useEffect(() => {
    onLockChange?.(serverLine)
  }, [onLockChange, serverLine])

  // antd never reports a close while disabled, so the open flag must be reset here.
  useEffect(() => {
    if (isReadOnly) setIsOpen(false)
  }, [isReadOnly])

  // Typing waits; the next key restarts the wait.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(draft), SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [draft])

  // A new patient drops the typed text and shows that patient's words.
  useEffect(() => {
    if (mode !== 'create') return
    setDraft('')
    setQuery('')
  }, [mode, patientId])

  const { currentData, isFetching, isError } = useSearchChiefComplaintsQuery(
    { patientId: patientId ?? 0, q: query },
    { skip: !patientId || !isOpen || isReadOnly },
  )
  const options = useMemo(() => buildOptions(currentData), [currentData])

  const getInput = () => document.getElementById(inputId) as HTMLInputElement | null

  // Rewording puts the words back in the box, selected.
  useEffect(() => {
    if (!list.rewordKey) return
    getInput()?.focus()
    getInput()?.select()
  }, [inputId, list.rewordKey])

  const setArrowed = (value: boolean) => {
    arrowedRef.current = value
    setHasArrowed(value)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) setArrowed(false)
    setIsOpen(open)
  }

  const commit = (text: string) => {
    // As OpenEMR's ccAdd: blank or repeated words outside a reword leave the box as it is.
    const words = toComplaintText(text)
    if (!list.rewordKey && (!words || hasComplaint(list.entries, words))) return
    if (list.rewordKey) list.finishReword(text)
    else list.add(text)
    setDraft('')
    setQuery('')
    setIsOpen(false)
    setArrowed(false)
  }

  // Runs before rc-select's own handler, so the pick below knows which key made it.
  const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    // Keys that belong to an open IME composition (Enter confirms it) are not ours or rc-select's.
    if (event.nativeEvent.isComposing) {
      event.stopPropagation()
      return
    }
    // rc-select takes Tab as Enter on the highlighted row and holds focus; kept from it, Tab only moves on.
    if (event.key === 'Tab') {
      event.stopPropagation()
      return
    }
    if (ARROW_KEYS.has(event.key)) setArrowed(true)
    if (event.key !== 'Enter') return
    enterRef.current = true
    queueMicrotask(() => {
      enterRef.current = false
    })
  }

  const handleSearch = (text: string) => {
    setArrowed(false)
    setDraft(text.slice(0, COMPLAINT_MAX_LENGTH))
  }

  const handleSelect = (value: string) => {
    // Enter picks only an option reached with the arrows; a hover or rc-select's own match keeps the typed words.
    if (enterRef.current && !arrowedRef.current) return
    pickedRef.current = true
    queueMicrotask(() => {
      pickedRef.current = false
    })
    commit(value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Enter never submits the booking; a pick made by the same key already committed.
      event.preventDefault()
      // rc-select fires onSelect synchronously before our onKeyDown, in the same keydown.
      if (pickedRef.current) return
      commit(draft)
      return
    }
    if (event.key !== 'Escape') return
    // The input is type=search: Chrome would clear it, and the dialog would close.
    event.preventDefault()
    if (!isOpen && !list.rewordKey) return
    event.stopPropagation()
    setIsOpen(false)
    if (list.rewordKey) {
      list.cancelReword()
      setDraft('')
    }
  }

  const startReword = (entry: ComplaintEntry) => {
    const words = entry.complaint.slice(0, COMPLAINT_MAX_LENGTH)
    list.startReword(entry.key)
    setDraft(words)
    setQuery(words)
  }

  const remove = (key: string) => {
    list.remove(key)
    getInput()?.focus()
  }

  // A failed search shows no list at all rather than claiming nothing was said.
  const notFound = !patientId ? (
    COMPLAINT_TEXT.choosePatient
  ) : isFetching ? (
    <Spin size='small' />
  ) : isError ? null : (
    COMPLAINT_TEXT.nothing
  )

  return (
    <div className={styles.field} onKeyDownCapture={handleKeyDownCapture}>
      <Label id={inputId} label={COMPLAINT_TEXT.label} className={styles.label} />
      <AutoComplete<string, Option | Group>
        id={inputId}
        size='large'
        className={styles.select}
        value={draft}
        options={options}
        open={isOpen}
        onOpenChange={(open) => !isReadOnly && handleOpenChange(open)}
        classNames={{ popup: { root: hasArrowed ? undefined : styles.passiveActive } }}
        onFocus={() => setQuery(draft)}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        notFoundContent={notFound}
        placeholder={COMPLAINT_TEXT.placeholder}
        title={COMPLAINT_TEXT.inputTitle}
        maxLength={COMPLAINT_MAX_LENGTH}
        disabled={isReadOnly}
        showAction={['focus']}
        defaultActiveFirstOption={false}
        filterOption={false}
        virtual={false}
        getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
      />
      {list.isPending && <Spin size='small' className={styles.pending} />}
      <ComplaintChips
        entries={list.entries}
        rewordKey={list.rewordKey}
        isReadOnly={isReadOnly}
        onRemove={remove}
        onReword={startReword}
      />
      {hintLine && <p className={styles.hint}>{hintLine}</p>}
    </div>
  )
}
